<template>
    <div ref="wrap" class="prm" :class="{ 'prm--fullscreen': fullscreen }">
        <div class="prm__stage">
            <div ref="map" class="prm__map" :class="{ 'prm__map--drawing': !!mode }" role="region" aria-label="Mapa do roteiro de visitas"></div>

            <!-- Ferramentas -->
            <div class="prm__toolbar" role="toolbar" aria-label="Ferramentas do mapa">
                <div class="prm__group">
                    <button type="button" class="prm__tool" :class="{ 'prm__tool--on': panel === 'search' }" title="Buscar cidade" aria-label="Buscar cidade" @click="togglePanel('search')"><v-icon small>mdi-magnify</v-icon></button>
                    <button type="button" class="prm__tool" :class="{ 'prm__tool--on': panel === 'layers' }" title="Camadas e filtros" aria-label="Camadas e filtros" @click="togglePanel('layers')"><v-icon small>mdi-layers-outline</v-icon></button>
                </div>
                <div class="prm__group">
                    <button type="button" class="prm__tool" title="Enquadrar o roteiro" aria-label="Enquadrar o roteiro" @click="fitAll"><v-icon small>mdi-fit-to-screen-outline</v-icon></button>
                    <button type="button" class="prm__tool" :disabled="!base" title="Centralizar na base" aria-label="Centralizar na base" @click="centerBase"><v-icon small>mdi-home-map-marker</v-icon></button>
                    <button type="button" class="prm__tool" :title="fullscreen ? 'Sair da tela cheia' : 'Tela cheia'" :aria-label="fullscreen ? 'Sair da tela cheia' : 'Tela cheia'" @click="toggleFullscreen">
                        <v-icon small>{{ fullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen" }}</v-icon>
                    </button>
                </div>
                <div class="prm__group">
                    <button v-if="editable" type="button" class="prm__tool" :class="{ 'prm__tool--on': mode === 'rect' }" title="Selecionar casos por retângulo" aria-label="Selecionar por retângulo" @click="setMode('rect')"><v-icon small>mdi-selection-drag</v-icon></button>
                    <button v-if="editable" type="button" class="prm__tool" :class="{ 'prm__tool--on': mode === 'lasso' }" title="Selecionar casos à mão livre (laço)" aria-label="Selecionar por laço" @click="setMode('lasso')"><v-icon small>mdi-lasso</v-icon></button>
                    <button type="button" class="prm__tool" :class="{ 'prm__tool--on': mode === 'measure' }" title="Medir distância" aria-label="Medir distância" @click="setMode('measure')"><v-icon small>mdi-ruler</v-icon></button>
                    <button type="button" class="prm__tool" :class="{ 'prm__tool--on': mode === 'radius' }" title="Raio a partir de um ponto" aria-label="Desenhar raio" @click="setMode('radius')"><v-icon small>mdi-radius-outline</v-icon></button>
                </div>
            </div>

            <!-- Painel: busca -->
            <div v-if="panel === 'search'" class="prm__panel">
                <div class="prm__panel-title">Buscar cidade</div>
                <input ref="search" v-model="searchTerm" class="prm__input" placeholder="Nome da cidade" aria-label="Nome da cidade" @input="searchCities" @keydown.esc="panel = null">
                <ul v-if="searchResults.length" class="prm__results">
                    <li v-for="city in searchResults" :key="city.id"><button type="button" @click="flyToCity(city)">{{ city.name }}/{{ city.uf }}</button></li>
                </ul>
                <p v-else-if="searchTerm.length >= 2 && !searching" class="prm__hint">Nenhuma cidade encontrada.</p>
            </div>

            <!-- Painel: camadas -->
            <div v-if="panel === 'layers'" class="prm__panel">
                <div class="prm__panel-title">Camadas</div>
                <label v-for="team in teams" :key="team.id" class="prm__check">
                    <input type="checkbox" :checked="!hiddenTeams.includes(team.id)" @change="toggleTeam(team.id)">
                    <i class="prm__swatch" :style="{ background: team.color }"></i>{{ team.name }}
                </label>
                <label class="prm__check"><input v-model="layers.outside" type="checkbox"><i class="prm__swatch prm__swatch--ring"></i>Casos fora do roteiro</label>
                <label class="prm__check"><input v-model="layers.airports" type="checkbox" :disabled="!airports.length"><v-icon x-small class="prm__check-icon">mdi-airplane</v-icon>Aeroportos</label>
                <label class="prm__check"><input v-model="layers.offices" type="checkbox" :disabled="!offices.length"><v-icon x-small class="prm__check-icon">mdi-office-building</v-icon>Sedes</label>
                <div class="prm__panel-title mt-2">Pontos de apoio · OpenStreetMap</div>
                <label class="prm__check"><input v-model="layers.hotels" type="checkbox"><v-icon x-small class="prm__check-icon" color="#0f766e">mdi-bed</v-icon>Hotéis nas cidades de pernoite</label>
                <label class="prm__check"><input v-model="layers.fuel" type="checkbox"><v-icon x-small class="prm__check-icon" color="#b45309">mdi-gas-station</v-icon>Postos ao longo da rota</label>
                <label class="prm__check"><input v-model="layers.food" type="checkbox"><v-icon x-small class="prm__check-icon" color="#be185d">mdi-silverware-fork-knife</v-icon>Restaurantes ao longo da rota</label>
                <p v-if="poisLoading" class="prm__hint">Buscando no OpenStreetMap… na primeira vez pode levar até 1 minuto.</p>
                <p v-else-if="poisError" class="prm__hint prm__hint--error">{{ poisError }} <button type="button" class="prm__link" @click="$emit('load-pois')">Tentar de novo</button></p>
                <p v-else-if="pois && wantsPois" class="prm__hint">{{ poiSummary }}</p>
                <p v-if="!poisLoading && pois && wantsPois && pois.errors && pois.errors.length" class="prm__hint prm__hint--error">
                    {{ pois.errors.join(" ") }} <button type="button" class="prm__link" @click="$emit('load-pois')">Tentar de novo</button>
                </p>
                <div class="prm__panel-title mt-2">Fora do roteiro: prioridade</div>
                <div class="prm__chips">
                    <button v-for="item in priorityItems" :key="item.value" type="button" class="prm__chip" :class="{ 'prm__chip--on': priorities[item.value] }" @click="priorities[item.value] = !priorities[item.value]">
                        <i :style="{ borderColor: item.color }"></i>{{ item.text }}
                    </button>
                </div>
            </div>

            <!-- Barra do modo ativo -->
            <div v-if="mode" class="prm__modebar">
                <template v-if="mode === 'rect' || mode === 'lasso'">
                    <span v-if="!selection">{{ mode === "rect" ? "Arraste para desenhar um retângulo." : "Desenhe em volta dos casos com o mouse pressionado." }}</span>
                    <template v-else>
                        <span><strong>{{ selection.outside.length + selection.inside.length }}</strong> {{ selection.outside.length + selection.inside.length === 1 ? "caso" : "casos" }} na área</span>
                        <button v-if="selection.outside.length && activeTeam" type="button" class="prm__action prm__action--primary" @click="applySelection('add')">Incluir {{ selection.outside.length }} em {{ activeTeam.name }}</button>
                        <button v-if="selection.inside.length" type="button" class="prm__action" @click="applySelection('remove')">Tirar {{ selection.inside.length }} do roteiro</button>
                    </template>
                </template>
                <template v-else-if="mode === 'measure'">
                    <span v-if="measurePoints.length < 2">Clique no mapa para marcar os pontos.</span>
                    <span v-else>Linha reta <strong>{{ formatKm(measureKm) }}</strong> · estrada ≈ {{ formatKm(measureKm * 1.2) }}</span>
                    <button v-if="measurePoints.length" type="button" class="prm__action" @click="clearMeasure">Limpar</button>
                </template>
                <template v-else-if="mode === 'radius'">
                    <span v-if="!radius.center">Clique no mapa (ou use a base) para o centro.</span>
                    <span v-else><strong>{{ radiusCount }}</strong> {{ radiusCount === 1 ? "caso" : "casos" }} até {{ radius.km }} km em linha reta</span>
                    <select v-model.number="radius.km" class="prm__select" aria-label="Raio em km" @change="drawTools">
                        <option v-for="km in [50, 100, 150, 200, 300, 500]" :key="km" :value="km">{{ km }} km</option>
                    </select>
                    <button v-if="base" type="button" class="prm__action" @click="radiusAtBase">Na base</button>
                </template>
                <button type="button" class="prm__close" aria-label="Fechar ferramenta" @click="setMode(null)"><v-icon small>mdi-close</v-icon></button>
            </div>
        </div>

        <div class="prm__legend">
            <span v-for="team in visibleTeams" :key="team.id"><i class="prm__line" :class="{ 'prm__line--straight': !team.geometry }" :style="{ borderTopColor: team.color }"></i>{{ team.name }}</span>
            <span v-if="layers.outside"><i class="prm__dot prm__dot--alta"></i>alta</span>
            <span v-if="layers.outside"><i class="prm__dot prm__dot--media"></i>média</span>
            <span v-if="layers.outside"><i class="prm__dot prm__dot--baixa"></i>baixa (fora do roteiro)</span>
            <span v-if="layers.airports && airports.length"><v-icon x-small>mdi-airplane</v-icon>aeroporto</span>
            <span v-if="layers.hotels && pois"><v-icon x-small color="#0f766e">mdi-bed</v-icon>hotel ({{ pois.hotels.length }})</span>
            <span v-if="layers.fuel && pois"><v-icon x-small color="#b45309">mdi-gas-station</v-icon>posto ({{ pois.fuel.length }})</span>
            <span v-if="layers.food && pois"><v-icon x-small color="#be185d">mdi-silverware-fork-knife</v-icon>restaurante ({{ pois.food.length }})</span>
            <span v-if="wantsPois && poisLoading" class="prm__loading-text">buscando pontos de apoio…</span>
            <span v-if="teams.some((team) => !team.geometry)"><i class="prm__line prm__line--straight"></i>em linha reta</span>
        </div>
    </div>
</template>

<script>
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const COLORS = { alta: "#dc2626", media: "#7c3aed", baixa: "#94a3b8" };
const POI_ICONS = { hotel: "mdi-bed", fuel: "mdi-gas-station", food: "mdi-silverware-fork-knife" };
// Zoom minimo de cada camada (nas cidades, restaurantes demais poluem o mapa).
const POI_MIN_ZOOM = { hotels: 0, fuel: 8, food: 10 };
// OpenStreetMap sem chave; o tom claro vem de um filtro na camada de tiles
// (.prm-tiles), sem afetar rotas e marcadores.
const TILES = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">colaboradores do OpenStreetMap</a>';

function escapeHtml(value) {
    return String(value == null ? "" : value).replace(/[&<>"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[char]));
}

function haversineKm(a, b) {
    const rad = (value) => (Number(value) * Math.PI) / 180;
    const dLat = rad(b.lat - a.lat);
    const dLng = rad(b.lng - a.lng);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
    return 2 * 6371 * Math.asin(Math.sqrt(h));
}

// Ponto dentro do poligono (laco), por raio.
function insidePolygon(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
        const a = polygon[i];
        const b = polygon[j];
        if ((a.lng > point.lng) !== (b.lng > point.lng) && point.lat < ((b.lat - a.lat) * (point.lng - a.lng)) / (b.lng - a.lng) + a.lat) inside = !inside;
    }
    return inside;
}

// Mapa do roteiro (OpenStreetMap em tom claro, sem chave): base,
// paradas numeradas na cor de cada equipe, demais casos pela prioridade,
// aeroportos e sedes, e o tracado pelas estradas quando o Waybuilder devolve
// a geometria. Ferramentas: busca de cidade, camadas e filtros, enquadrar,
// tela cheia, selecao por retangulo/laco (eventos add-cases / remove-cases),
// medir distancia e raio.
export default {
    name: "ProspectRouteMap",

    props: {
        base: { type: Object, default: null }, // { id, name, uf, lat, lng }
        // [{ id, name, uf, lat, lng, priority, cases: [{ id, company_name, team_id }] }]
        cities: { type: Array, default: () => [] },
        // [{ id, name, color, order: [cityId], geometry: [[lat, lng]] | null }]
        teams: { type: Array, default: () => [] },
        activeTeam: { type: Object, default: null }, // { id, name, color }
        airports: { type: Array, default: () => [] }, // [{ id, iata, name, city, uf, lat, lng }]
        arrivalAirportId: { type: Number, default: null },
        offices: { type: Array, default: () => [] }, // [{ id, name, lat, lng }]
        // Pontos de apoio do OpenStreetMap: { hotels: [], fuel: [], food: [] } (null = nao carregado).
        pois: { type: Object, default: null },
        poisLoading: { type: Boolean, default: false },
        poisError: { type: String, default: null },
        roundTrip: { type: Boolean, default: true },
        editable: { type: Boolean, default: false },
        fitKey: { type: String, default: "" }, // muda -> reenquadra
    },

    data: () => ({
        map: null,
        layer: null,
        toolLayer: null,
        fittedKey: null,
        openCityId: null,
        redrawing: false,
        fullscreen: false,
        zoom: 4,
        panel: null,
        mode: null,
        hiddenTeams: [],
        layers: { outside: true, airports: false, offices: true, hotels: false, fuel: false, food: false },
        priorities: { alta: true, media: true, baixa: true },
        priorityItems: [{ value: "alta", text: "Alta", color: COLORS.alta }, { value: "media", text: "Média", color: COLORS.media }, { value: "baixa", text: "Baixa", color: COLORS.baixa }],
        searchTerm: "",
        searchResults: [],
        searching: false,
        searchTimer: null,
        drawing: null,
        selection: null,
        measurePoints: [],
        radius: { center: null, km: 150 },
    }),

    computed: {
        wantsPois() {
            return this.layers.hotels || this.layers.fuel || this.layers.food;
        },

        poiSummary() {
            const parts = [];
            if (this.layers.hotels) parts.push(`${this.pois.hotels.length} hotéis`);
            if (this.layers.fuel) parts.push(`${this.pois.fuel.length} postos`);
            if (this.layers.food) parts.push(`${this.pois.food.length} restaurantes`);
            const hidden = [];
            if (this.layers.fuel && this.zoom < POI_MIN_ZOOM.fuel) hidden.push("postos");
            if (this.layers.food && this.zoom < POI_MIN_ZOOM.food) hidden.push("restaurantes");
            const source = { waybuilder: "cópia do OpenStreetMap no Waybuilder", overpass: "OpenStreetMap (servidores públicos)", mixed: "Waybuilder e OpenStreetMap" }[this.pois.source];
            return `${parts.join(" · ")}.${hidden.length ? ` Aproxime o mapa para ver ${hidden.join(" e ")}.` : ""}${source ? ` Fonte: ${source}.` : ""} Dados colaborativos: confira antes de reservar.`;
        },

        visibleTeams() {
            return this.teams.filter((team) => !this.hiddenTeams.includes(team.id));
        },

        measureKm() {
            return this.measurePoints.reduce((sum, point, index) => (index ? sum + haversineKm(this.measurePoints[index - 1], point) : 0), 0);
        },

        radiusCount() {
            if (!this.radius.center) return 0;
            return this.cities.reduce((sum, city) => sum + (haversineKm(this.radius.center, city) <= this.radius.km ? (city.cases || []).length : 0), 0);
        },
    },

    watch: {
        base: "draw",
        cities() {
            this.draw();
            if (this.selection) this.selection = null;
            if (this.radius.center) this.drawTools();
        },
        teams: "draw",
        activeTeam: "draw",
        airports: "draw",
        arrivalAirportId: "draw",
        offices: "draw",
        roundTrip: "draw",
        editable: "draw",
        fitKey: "draw",
        hiddenTeams: "draw",
        layers: {
            deep: true,
            handler() {
                this.draw();
                this.requestPois();
            },
        },
        pois() {
            this.draw();
            this.requestPois();
        },
        priorities: { deep: true, handler: "draw" },
    },

    mounted() {
        this.map = L.map(this.$refs.map, { scrollWheelZoom: true, attributionControl: true, zoomControl: false }).setView([-15.8, -47.9], 4);
        L.control.zoom({ position: "bottomright" }).addTo(this.map);
        L.tileLayer(TILES, { maxZoom: 19, attribution: ATTRIBUTION, className: "prm-tiles" }).addTo(this.map);
        this.layer = L.layerGroup().addTo(this.map);
        this.toolLayer = L.layerGroup().addTo(this.map);
        this.map.on("popupclose", () => {
            if (!this.redrawing) this.openCityId = null;
        });
        this.map.on("mousedown", this.onDown);
        this.map.on("mousemove", this.onMove);
        this.map.on("mouseup", this.onUp);
        this.map.on("click", this.onClick);
        this.map.on("zoomend", () => {
            this.zoom = this.map.getZoom();
            if (this.pois && (this.layers.fuel || this.layers.food)) this.draw();
        });
        document.addEventListener("keydown", this.onKey);
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.map && this.map.invalidateSize(false));
            this.resizeObserver.observe(this.$refs.map);
        }
        this.draw();
        // O mapa nasce dentro de um cartao que ainda pode estar mudando de tamanho.
        this.$nextTick(() => setTimeout(() => {
            if (!this.map) return;
            this.map.invalidateSize();
            this.fit(this.lastBounds);
        }, 150));
    },

    beforeDestroy() {
        if (this.fullscreen) document.body.style.overflow = "";
        document.removeEventListener("keydown", this.onKey);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.map) this.map.remove();
        this.map = null;
    },

    methods: {
        formatKm(km) {
            return `${Math.round(km).toLocaleString("pt-BR")} km`;
        },

        icon(html, className, size = null) {
            return L.divIcon({ html, className: `prm-icon ${className}`, iconSize: size });
        },

        teamOf(id) {
            return this.teams.find((team) => team.id === id) || null;
        },

        // ---------------------------------------------------------------
        // Desenho
        // ---------------------------------------------------------------
        // Popup montado em DOM para os botoes chamarem o componente.
        popup(city, title) {
            const root = document.createElement("div");
            root.className = "prm__popup";
            const heading = document.createElement("div");
            heading.className = "prm__popup-title";
            heading.textContent = title;
            root.appendChild(heading);
            const list = document.createElement("ul");
            (city.cases || []).forEach((item) => {
                const row = document.createElement("li");
                const team = item.team_id ? this.teamOf(item.team_id) : null;
                const dot = document.createElement("i");
                dot.className = "prm__popup-dot";
                dot.style.background = team ? team.color : "transparent";
                dot.style.borderColor = team ? team.color : COLORS[city.priority] || COLORS.baixa;
                dot.title = team ? team.name : "fora do roteiro";
                row.appendChild(dot);
                const link = document.createElement("a");
                link.href = `#/prospeccao/casos/${item.id}`;
                link.textContent = item.company_name;
                row.appendChild(link);
                if (this.editable) {
                    const button = document.createElement("button");
                    button.type = "button";
                    button.className = `prm__popup-btn${item.team_id ? " prm__popup-btn--remove" : ""}`;
                    button.textContent = item.team_id ? "Tirar" : "Incluir";
                    button.addEventListener("click", () => this.$emit(item.team_id ? "remove-cases" : "add-cases", [item.id]));
                    row.appendChild(button);
                }
                list.appendChild(row);
            });
            root.appendChild(list);
            const outside = (city.cases || []).filter((item) => !item.team_id);
            if (this.editable && this.activeTeam && outside.length > 1) {
                const all = document.createElement("button");
                all.type = "button";
                all.className = "prm__popup-all";
                all.textContent = `Incluir os ${outside.length} em ${this.activeTeam.name}`;
                all.addEventListener("click", () => this.$emit("add-cases", outside.map((item) => item.id)));
                root.appendChild(all);
            } else if (this.editable && this.activeTeam && outside.length === 1) {
                const hint = document.createElement("div");
                hint.className = "prm__popup-hint";
                hint.textContent = `Incluir leva para ${this.activeTeam.name}.`;
                root.appendChild(hint);
            }
            return root;
        },

        requestPois() {
            if (this.wantsPois && !this.pois && !this.poisLoading && !this.poisError) this.$emit("load-pois");
        },

        poiPopup(item) {
            const root = document.createElement("div");
            root.className = "prm__popup";
            const add = (className, text, tag = "div") => {
                const node = document.createElement(tag);
                node.className = className;
                node.textContent = text;
                root.appendChild(node);
                return node;
            };
            add("prm__popup-title", item.name);
            const kind = { hotel: "Hotel", fuel: "Posto de combustível", food: "Restaurante" }[item.kind];
            add("prm__popup-sub", [kind, item.brand && item.brand !== item.name ? item.brand : null, item.stars ? `${item.stars} estrela${item.stars > 1 ? "s" : ""}` : null, item.cuisine, item.truck ? "atende caminhão" : null].filter(Boolean).join(" · "));
            if (item.km !== undefined && item.km !== null) add("prm__popup-line", `km ${item.km.toLocaleString("pt-BR")} da rota${item.offset_km ? `, a ${item.offset_km.toLocaleString("pt-BR")} km da pista` : ""}${item.team_name ? ` · ${item.team_name}` : ""}`);
            if (item.city_name) add("prm__popup-line", `${item.city_name}${item.distance_km ? ` · ${item.distance_km.toLocaleString("pt-BR")} km do centro` : ""}${item.nights ? ` · pernoite no dia ${item.nights.join(", ")}` : ""}`);
            if (item.address) add("prm__popup-line", item.address);
            if (item.opening_hours) add("prm__popup-line", `Horário: ${item.opening_hours}`);
            const links = document.createElement("div");
            links.className = "prm__popup-links";
            const link = (href, text) => {
                const anchor = document.createElement("a");
                anchor.href = href;
                anchor.target = "_blank";
                anchor.rel = "noopener";
                anchor.textContent = text;
                links.appendChild(anchor);
            };
            if (item.phone) link(`tel:${String(item.phone).replace(/[^\d+]/g, "")}`, item.phone);
            if (item.website && /^https?:\/\//.test(item.website)) link(item.website, "Site");
            link(`https://www.google.com/maps/search/?api=1&query=${item.lat},${item.lng}`, "Abrir no Google Maps");
            root.appendChild(links);
            return root;
        },

        draw() {
            if (!this.map || !this.layer) return;
            this.redrawing = true;
            this.layer.clearLayers();
            const bounds = [];
            const markers = new Map();
            const visible = this.visibleTeams;
            const stopOf = new Map();
            visible.forEach((team) => {
                (team.order || []).forEach((cityId, index) => {
                    if (!stopOf.has(cityId)) stopOf.set(cityId, { team, position: index + 1 });
                });
            });
            const visibleIds = new Set(visible.map((team) => team.id));

            // Tracados primeiro (ficam por baixo dos marcadores).
            const byId = new Map(this.cities.map((city) => [city.id, city]));
            visible.forEach((team) => {
                if (!this.base || !(team.order || []).length) return;
                let line = team.geometry && team.geometry.length > 1 ? team.geometry : null;
                if (!line) {
                    const points = [this.base, ...team.order.map((id) => byId.get(id)).filter(Boolean)];
                    if (this.roundTrip) points.push(this.base);
                    line = points.map((point) => [point.lat, point.lng]);
                }
                const active = this.activeTeam && this.activeTeam.id === team.id;
                const strong = active || this.teams.length === 1;
                L.polyline(line, { color: "#ffffff", weight: strong ? 8 : 6, opacity: 0.9, interactive: false }).addTo(this.layer);
                L.polyline(line, { color: team.color, weight: strong ? 4.5 : 3, opacity: strong ? 0.95 : 0.6, dashArray: team.geometry ? null : "6 8", interactive: false }).addTo(this.layer);
                line.forEach((point) => bounds.push(point));
            });

            if (this.layers.offices) {
                this.offices.forEach((office) => {
                    if (this.base && Math.abs(office.lat - this.base.lat) < 1e-6 && Math.abs(office.lng - this.base.lng) < 1e-6) return;
                    L.marker([office.lat, office.lng], { icon: this.icon('<span><i class="mdi mdi-office-building"></i></span>', "prm-icon--office"), zIndexOffset: 300 })
                        .bindTooltip(`Sede ${escapeHtml(office.name)}`, { direction: "top", offset: [0, -12] })
                        .addTo(this.layer);
                });
            }

            if (this.layers.airports || this.arrivalAirportId) {
                this.airports.forEach((airport) => {
                    const arrival = airport.id === this.arrivalAirportId;
                    if (!this.layers.airports && !arrival) return;
                    L.marker([airport.lat, airport.lng], { icon: this.icon(`<span><i class="mdi mdi-airplane"></i>${arrival ? `<b>${escapeHtml(airport.iata)}</b>` : ""}</span>`, `prm-icon--airport${arrival ? " prm-icon--arrival" : ""}`), zIndexOffset: arrival ? 900 : 200 })
                        .bindTooltip(`${escapeHtml(airport.iata)} · ${escapeHtml(airport.name)} (${escapeHtml(airport.city)}/${escapeHtml(airport.uf)})${arrival ? " · chegada" : ""}`, { direction: "top", offset: [0, -12] })
                        .addTo(this.layer);
                });
            }

            if (this.pois) {
                const zoom = this.map.getZoom();
                [["hotels", "hotel"], ["fuel", "fuel"], ["food", "food"]].forEach(([layer, kind]) => {
                    if (!this.layers[layer] || zoom < POI_MIN_ZOOM[layer]) return;
                    (this.pois[layer] || []).forEach((item) => {
                        L.marker([item.lat, item.lng], { icon: this.icon(`<span><i class="mdi ${POI_ICONS[kind]}"></i></span>`, `prm-icon--poi prm-icon--${kind}`), zIndexOffset: 150 })
                            .bindTooltip(escapeHtml(item.name), { direction: "top", offset: [0, -10] })
                            .bindPopup(() => this.poiPopup(item), { minWidth: 220, className: "prm-popup" })
                            .addTo(this.layer);
                    });
                });
            }

            this.cities.forEach((city) => {
                if (this.base && city.id === this.base.id) return;
                const stop = stopOf.get(city.id);
                const inHiddenTeam = !stop && (city.cases || []).some((item) => item.team_id) && (city.cases || []).every((item) => !item.team_id || !visibleIds.has(item.team_id));
                if (inHiddenTeam) return;
                if (!stop && (!this.layers.outside || !this.priorities[city.priority || "baixa"])) return;
                const color = COLORS[city.priority] || COLORS.baixa;
                const count = (city.cases || []).length;
                const marker = stop
                    ? L.marker([city.lat, city.lng], { icon: this.icon(`<span style="background:${stop.team.color}">${stop.position}</span>`, "prm-icon--stop"), zIndexOffset: 500 })
                    : L.marker([city.lat, city.lng], { icon: this.icon(`<span style="border-color:${color}">${count > 1 ? count : ""}</span>`, `prm-icon--case${count > 1 ? " prm-icon--many" : ""}`), zIndexOffset: 100 });
                marker.bindPopup(() => this.popup(city, `${stop ? `${stop.position}. ` : ""}${city.name}/${city.uf}`), { minWidth: 240, className: "prm-popup" });
                marker.bindTooltip(`${escapeHtml(city.name)}${count > 1 ? ` · ${count} casos` : ""}`, { direction: "top", offset: [0, -12], opacity: 0.95 });
                marker.on("popupopen", () => {
                    this.openCityId = city.id;
                });
                marker.addTo(this.layer);
                markers.set(city.id, marker);
                bounds.push([city.lat, city.lng]);
            });

            if (this.base) {
                const baseCity = this.cities.find((city) => city.id === this.base.id) || { ...this.base, cases: [] };
                const marker = L.marker([this.base.lat, this.base.lng], { icon: this.icon('<span><i class="mdi mdi-home-variant"></i></span>', "prm-icon--base"), zIndexOffset: 1000 })
                    .bindPopup(() => this.popup(baseCity, `Base: ${this.base.name}/${this.base.uf}`), { minWidth: 240, className: "prm-popup" })
                    .bindTooltip(`Base: ${escapeHtml(this.base.name)}`, { direction: "top", offset: [0, -14], opacity: 0.95 })
                    .on("popupopen", () => {
                        this.openCityId = this.base.id;
                    })
                    .addTo(this.layer);
                markers.set(this.base.id, marker);
                bounds.push([this.base.lat, this.base.lng]);
            }

            this.lastBounds = bounds;
            // Reenquadra quando muda a chave ou quando os casos chegam depois da base.
            const key = this.fitKey || "default";
            if (bounds.length && (this.fittedKey !== key || (this.fittedCount <= 1 && bounds.length > 1))) {
                this.fittedKey = key;
                this.fittedCount = bounds.length;
                this.fit(bounds);
            }
            this.redrawing = false;
            // Mantem aberto o popup em que o usuario estava incluindo/tirando casos.
            if (this.openCityId && markers.has(this.openCityId)) markers.get(this.openCityId).openPopup();
        },

        fit(bounds) {
            if (!this.map || !bounds || !bounds.length) return;
            // O cartao pode ter mudado de tamanho desde a criacao do mapa.
            this.map.invalidateSize(false);
            if (!this.map.getSize().x) return;
            if (bounds.length === 1) this.map.setView(bounds[0], 9);
            else this.map.fitBounds(bounds, { padding: [48, 48], maxZoom: 10 });
        },

        // ---------------------------------------------------------------
        // Ferramentas simples
        // ---------------------------------------------------------------
        togglePanel(name) {
            this.panel = this.panel === name ? null : name;
            if (this.panel === "search") this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
        },

        toggleTeam(id) {
            this.hiddenTeams = this.hiddenTeams.includes(id) ? this.hiddenTeams.filter((item) => item !== id) : [...this.hiddenTeams, id];
        },

        fitAll() {
            this.fit(this.lastBounds);
        },

        centerBase() {
            if (this.base) this.map.setView([this.base.lat, this.base.lng], 9);
        },

        // Tela cheia por CSS (camada fixa sobre a pagina): funciona em qualquer
        // navegador, inclusive dentro de iframes que bloqueiam a API.
        toggleFullscreen() {
            this.fullscreen = !this.fullscreen;
            document.body.style.overflow = this.fullscreen ? "hidden" : "";
            this.$nextTick(() => this.map && this.map.invalidateSize());
        },

        onKey(event) {
            if (event.key !== "Escape") return;
            if (this.mode) this.setMode(null);
            else if (this.fullscreen) this.toggleFullscreen();
        },

        searchCities() {
            clearTimeout(this.searchTimer);
            const term = this.searchTerm.trim();
            if (term.length < 2) {
                this.searchResults = [];
                return;
            }
            this.searching = true;
            this.searchTimer = setTimeout(async () => {
                try {
                    const result = await axios.get("/api/admin/prospect/municipalities", { params: { q: term } });
                    this.searchResults = result.data.content.slice(0, 8);
                } catch (error) {
                    this.searchResults = [];
                } finally {
                    this.searching = false;
                }
            }, 250);
        },

        flyToCity(city) {
            this.map.flyTo([city.lat, city.lng], 10, { duration: 0.8 });
            const pulse = L.marker([city.lat, city.lng], { icon: this.icon("<span></span>", "prm-icon--pulse"), interactive: false }).addTo(this.toolLayer);
            setTimeout(() => this.toolLayer && this.toolLayer.removeLayer(pulse), 4000);
            this.panel = null;
        },

        // ---------------------------------------------------------------
        // Modos: selecao, medida e raio
        // ---------------------------------------------------------------
        setMode(mode) {
            const next = this.mode === mode ? null : mode;
            this.mode = next;
            this.panel = null;
            this.selection = null;
            this.drawing = null;
            if (next !== "measure") this.measurePoints = [];
            if (next !== "radius") this.radius = { ...this.radius, center: null };
            if (next === "rect" || next === "lasso") this.map.dragging.disable();
            else this.map.dragging.enable();
            if (next) this.map.closePopup();
            this.drawTools();
        },

        onDown(event) {
            if (this.mode !== "rect" && this.mode !== "lasso") return;
            this.selection = null;
            this.drawing = { start: event.latlng, points: [event.latlng] };
            this.drawTools();
        },

        onMove(event) {
            if (!this.drawing) return;
            if (this.mode === "rect") {
                this.drawing.end = event.latlng;
            } else {
                const last = this.drawing.points[this.drawing.points.length - 1];
                const a = this.map.latLngToContainerPoint(last);
                const b = this.map.latLngToContainerPoint(event.latlng);
                if (Math.abs(a.x - b.x) + Math.abs(a.y - b.y) > 4) this.drawing.points.push(event.latlng);
            }
            this.drawTools();
        },

        onUp() {
            if (!this.drawing) return;
            const drawing = this.drawing;
            this.drawing = null;
            let test;
            if (this.mode === "rect") {
                if (!drawing.end) return this.drawTools();
                const area = L.latLngBounds(drawing.start, drawing.end);
                test = (city) => area.contains([city.lat, city.lng]);
                this.selectionShape = { type: "rect", bounds: area };
            } else {
                if (drawing.points.length < 3) return this.drawTools();
                test = (city) => insidePolygon({ lat: Number(city.lat), lng: Number(city.lng) }, drawing.points);
                this.selectionShape = { type: "lasso", points: drawing.points };
            }
            const visibleIds = new Set(this.visibleTeams.map((team) => team.id));
            const inside = [];
            const outside = [];
            this.cities.filter(test).forEach((city) => {
                (city.cases || []).forEach((item) => {
                    if (item.team_id) {
                        if (visibleIds.has(item.team_id)) inside.push(item.id);
                    } else if (this.layers.outside && this.priorities[city.priority || "baixa"]) {
                        outside.push(item.id);
                    }
                });
            });
            this.selection = { inside, outside };
            this.drawTools();
            return null;
        },

        applySelection(kind) {
            if (!this.selection) return;
            if (kind === "add") this.$emit("add-cases", this.selection.outside);
            else this.$emit("remove-cases", this.selection.inside);
            this.selection = null;
            this.selectionShape = null;
            this.drawTools();
        },

        onClick(event) {
            if (this.mode === "measure") {
                this.measurePoints = [...this.measurePoints, { lat: event.latlng.lat, lng: event.latlng.lng }];
                this.drawTools();
            } else if (this.mode === "radius") {
                this.radius = { ...this.radius, center: { lat: event.latlng.lat, lng: event.latlng.lng } };
                this.drawTools();
            }
        },

        clearMeasure() {
            this.measurePoints = [];
            this.drawTools();
        },

        radiusAtBase() {
            if (!this.base) return;
            this.radius = { ...this.radius, center: { lat: this.base.lat, lng: this.base.lng } };
            this.drawTools();
        },

        drawTools() {
            if (!this.toolLayer) return;
            this.toolLayer.clearLayers();
            const style = { color: "#5b21b6", weight: 2, fillColor: "#7c3aed", fillOpacity: 0.08, dashArray: "4 4", interactive: false };
            if (this.mode === "rect" || this.mode === "lasso") {
                if (this.drawing && this.mode === "rect" && this.drawing.end) L.rectangle(L.latLngBounds(this.drawing.start, this.drawing.end), style).addTo(this.toolLayer);
                if (this.drawing && this.mode === "lasso" && this.drawing.points.length > 1) L.polygon(this.drawing.points, style).addTo(this.toolLayer);
                if (!this.drawing && this.selection && this.selectionShape) {
                    if (this.selectionShape.type === "rect") L.rectangle(this.selectionShape.bounds, style).addTo(this.toolLayer);
                    else L.polygon(this.selectionShape.points, style).addTo(this.toolLayer);
                }
            }
            if (this.mode === "measure" && this.measurePoints.length) {
                const line = this.measurePoints.map((point) => [point.lat, point.lng]);
                L.polyline(line, { color: "#0f172a", weight: 3, dashArray: "2 6", interactive: false }).addTo(this.toolLayer);
                let total = 0;
                this.measurePoints.forEach((point, index) => {
                    if (index) total += haversineKm(this.measurePoints[index - 1], point);
                    const marker = L.circleMarker([point.lat, point.lng], { radius: 4, color: "#0f172a", weight: 2, fillColor: "#ffffff", fillOpacity: 1, interactive: false }).addTo(this.toolLayer);
                    if (index) marker.bindTooltip(this.formatKm(total), { permanent: true, direction: "right", className: "prm-measure-label", offset: [6, 0] });
                });
            }
            if (this.mode === "radius" && this.radius.center) {
                L.circle([this.radius.center.lat, this.radius.center.lng], { radius: this.radius.km * 1000, ...style, dashArray: null }).addTo(this.toolLayer);
                L.circleMarker([this.radius.center.lat, this.radius.center.lng], { radius: 4, color: "#5b21b6", weight: 2, fillColor: "#ffffff", fillOpacity: 1, interactive: false }).addTo(this.toolLayer);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.prm__stage {
    position: relative;
}

.prm__map {
    height: 520px;
    border-radius: $ui-radius-sm;
    border: 1px solid $ui-border;
    overflow: hidden;
    background: #f1f5f9;
    z-index: 0;

    @media (max-width: 760px) {
        height: 380px;
    }
}

.prm__map--drawing {
    cursor: crosshair;

    // Com uma ferramenta ativa, o clique vai para o mapa (nao abre popup).
    ::v-deep .leaflet-marker-icon,
    ::v-deep .leaflet-interactive {
        pointer-events: none !important;
    }

    ::v-deep .leaflet-container,
    ::v-deep .leaflet-grab {
        cursor: crosshair;
    }
}

.prm--fullscreen {
    position: fixed;
    inset: 0;
    z-index: 2000;
    padding: 12px;
    background: #ffffff;
    display: flex;
    flex-direction: column;

    .prm__stage {
        flex: 1 1 auto;
    }

    .prm__map {
        height: 100%;
    }
}

.prm__toolbar {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 500;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.prm__group {
    display: flex;
    flex-direction: column;
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(15, 23, 42, 0.08);
    overflow: hidden;
}

.prm__tool {
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border: 0;
    cursor: pointer;

    & + & {
        border-top: 1px solid #eef2f7;
    }

    &:hover {
        background: #f5f3ff;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }

    .v-icon {
        color: #334155;
    }
}

.prm__tool--on {
    background: #ede9fe;

    .v-icon {
        color: #5b21b6;
    }
}

.prm__panel {
    position: absolute;
    top: 12px;
    right: 56px;
    z-index: 500;
    width: 260px;
    max-height: calc(100% - 24px);
    overflow-y: auto;
    padding: 12px;
    background: #ffffff;
    border-radius: 10px;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.14);
    font-size: 13px;
}

.prm__panel-title {
    margin-bottom: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $ui-muted;
}

.prm__input {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid $ui-border;
    border-radius: 8px;
    outline: none;

    &:focus {
        border-color: #7c3aed;
    }
}

.prm__results {
    margin: 6px 0 0;
    padding: 0;
    list-style: none;

    button {
        width: 100%;
        padding: 5px 8px;
        text-align: left;
        border-radius: 6px;
        background: transparent;
        border: 0;
        cursor: pointer;

        &:hover {
            background: #f5f3ff;
        }
    }
}

.prm__hint {
    margin: 6px 0 0;
    font-size: 12px;
    color: $ui-muted;
}

.prm__hint--error {
    color: #b91c1c;
}

.prm__link {
    padding: 0;
    border: 0;
    background: transparent;
    color: #5b21b6;
    text-decoration: underline;
    cursor: pointer;
}

.prm__loading-text {
    font-style: italic;
}

.prm__check {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px 0;
    cursor: pointer;
}

.prm__check-icon {
    width: 10px;
}

.prm__swatch {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
}

.prm__swatch--ring {
    border: 2px solid #94a3b8;
    background: #ffffff;
}

.prm__chips {
    display: flex;
    gap: 6px;
}

.prm__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 2px 8px;
    font-size: 12px;
    border: 1px solid $ui-border;
    border-radius: 999px;
    background: #ffffff;
    color: $ui-muted;
    cursor: pointer;

    i {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        border: 2px solid;
    }
}

.prm__chip--on {
    color: $ui-fg;
    border-color: #c4b5fd;
    background: #faf5ff;
}

.prm__modebar {
    position: absolute;
    left: 50%;
    bottom: 16px;
    z-index: 500;
    transform: translateX(-50%);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 8px;
    max-width: calc(100% - 32px);
    padding: 8px 8px 8px 14px;
    font-size: 13px;
    background: #0f172a;
    color: #ffffff;
    border-radius: 999px;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.3);
}

.prm__action {
    padding: 3px 10px;
    font-size: 12px;
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 999px;
    background: transparent;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.12);
    }
}

.prm__action--primary {
    border-color: #a78bfa;
    background: #7c3aed;

    &:hover {
        background: #6d28d9;
    }
}

.prm__select {
    padding: 2px 6px;
    font-size: 12px;
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 999px;
    background: transparent;

    option {
        color: #0f172a;
    }
}

.prm__close {
    display: flex;
    width: 26px;
    height: 26px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.12);
    cursor: pointer;

    .v-icon {
        color: #ffffff;
    }
}

.prm__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 14px;
    margin-top: 8px;
    font-size: 12px;
    color: $ui-muted;

    span {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }
}

.prm__dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid;
    background: #ffffff;
}

.prm__dot--alta {
    border-color: #dc2626;
}

.prm__dot--media {
    border-color: #7c3aed;
}

.prm__dot--baixa {
    border-color: #94a3b8;
}

.prm__line {
    display: inline-block;
    width: 22px;
    border-top: 3px solid #150030;
}

.prm__line--straight {
    border-top-style: dashed;
}

::v-deep .leaflet-container {
    font-family: inherit;
    background: #f1f5f9;
}

::v-deep .prm-tiles {
    // Tom claro sem apagar as estradas (precisam aparecer para conferir o tracado).
    filter: grayscale(0.45) saturate(0.85) brightness(1.03) contrast(0.97);
}

::v-deep .leaflet-bar a {
    color: #334155;
}

::v-deep .leaflet-control-zoom {
    border: 0;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(15, 23, 42, 0.08);
}

::v-deep .leaflet-tooltip {
    padding: 3px 8px;
    font-size: 12px;
    border: 0;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.15);
}

::v-deep .prm-measure-label {
    font-weight: 600;
    background: #0f172a;
    color: #ffffff;

    &::before {
        display: none;
    }
}

::v-deep .prm-popup .leaflet-popup-content-wrapper {
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.18);
}

::v-deep .prm-popup .leaflet-popup-content {
    margin: 12px 14px;
}

::v-deep .prm-icon {
    background: transparent;
    border: 0;

    span {
        display: flex;
        align-items: center;
        justify-content: center;
        transform: translate(-50%, -50%);
        color: #ffffff;
        font-size: 12px;
        font-weight: 700;
    }
}

::v-deep .prm-icon--stop span {
    width: 26px;
    height: 26px;
    border: 2.5px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.35);
}

::v-deep .prm-icon--case span {
    width: 14px;
    height: 14px;
    border: 3px solid;
    border-radius: 50%;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
}

::v-deep .prm-icon--many span {
    width: 20px;
    height: 20px;
    font-size: 10px;
    color: #0f172a;
}

::v-deep .prm-icon--base span {
    width: 30px;
    height: 30px;
    font-size: 16px;
    border: 2.5px solid #ffffff;
    border-radius: 9px;
    background: #150030;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.4);
}

::v-deep .prm-icon--airport span {
    gap: 3px;
    min-width: 22px;
    height: 22px;
    padding: 0 5px;
    font-size: 13px;
    color: #0369a1;
    border: 1.5px solid #bae6fd;
    border-radius: 999px;
    background: #f0f9ff;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.2);

    b {
        font-size: 11px;
    }
}

::v-deep .prm-icon--arrival span {
    color: #ffffff;
    border-color: #ffffff;
    background: #0369a1;
}

::v-deep .prm-icon--poi span {
    width: 20px;
    height: 20px;
    font-size: 12px;
    border: 1.5px solid #ffffff;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.3);
}

::v-deep .prm-icon--hotel span {
    background: #0f766e;
}

::v-deep .prm-icon--fuel span {
    background: #b45309;
}

::v-deep .prm-icon--food span {
    background: #be185d;
}

::v-deep .prm-icon--office span {
    width: 24px;
    height: 24px;
    font-size: 13px;
    color: #150030;
    border: 1.5px solid #150030;
    border-radius: 7px;
    background: #ffffff;
}

::v-deep .prm-icon--pulse span {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(124, 58, 237, 0.35);
    border: 2px solid #7c3aed;
    animation: prm-pulse 1.2s ease-out infinite;
}

@keyframes prm-pulse {
    from {
        box-shadow: 0 0 0 0 rgba(124, 58, 237, 0.5);
    }

    to {
        box-shadow: 0 0 0 18px rgba(124, 58, 237, 0);
    }
}

::v-deep .prm__popup {
    font-size: 13px;

    ul {
        margin: 8px 0 0;
        padding: 0;
        list-style: none;
    }

    li {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 4px 0;
        border-top: 1px solid #f1f5f9;

        a {
            flex: 1 1 auto;
            color: #0f172a;
            font-weight: 500;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
            }
        }
    }
}

::v-deep .prm__popup-title {
    font-weight: 600;
    font-size: 14px;
}

::v-deep .prm__popup-sub {
    margin-top: 2px;
    font-size: 12px;
    color: #64748b;
}

::v-deep .prm__popup-line {
    margin-top: 4px;
    font-size: 12px;
}

::v-deep .prm__popup-links {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 10px;
    margin-top: 8px;
    font-size: 12px;

    a {
        color: #5b21b6;
    }
}

::v-deep .prm__popup-dot {
    flex: 0 0 10px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid;
}

::v-deep .prm__popup-btn,
::v-deep .prm__popup-all {
    border: 1px solid #c4b5fd;
    border-radius: 999px;
    padding: 1px 10px;
    font-size: 12px;
    color: #5b21b6;
    background: #f5f3ff;
    cursor: pointer;
}

::v-deep .prm__popup-btn--remove {
    border-color: #e2e8f0;
    color: #475569;
    background: #f8fafc;
}

::v-deep .prm__popup-all {
    margin-top: 8px;
    width: 100%;
    padding: 5px 8px;
}

::v-deep .prm__popup-hint {
    margin-top: 6px;
    font-size: 12px;
    color: #64748b;
}
</style>
